import React, { useCallback, useEffect, useState } from 'react';
import RichBodyEditor from '../RichBodyEditor';
import TemplateLibrary from '../TemplateLibrary';
import MatchedIntroPreviewTabs from './MatchedIntroPreviewTabs';

// A cluster's own display name — e.g. "Charlotte & Freddie" for a pair, or
// "Charlotte, Freddie & Priya" for a group — used both in the card heading
// and as a default subject-line fragment for the intro email.
const clusterNames = (members) => {
  const names = members.map((m) => m.firstName || 'Someone');
  if (names.length <= 1) return names[0] || '';
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
};

// Same photo/placeholder classes the member-selection grid above (in
// GranularInterconnectingEmails.js) already uses, just one per cluster
// member in a row instead of one per card.
const ClusterPhotos = ({ members }) => (
  <div className="as-signup-photo-gallery">
    {members.map((m) => (
      m.photo
        ? <img key={m.userId} src={m.photo} alt="" className="as-signup-img" />
        : <div key={m.userId} className="as-signup-img-placeholder">?</div>
    ))}
  </div>
);

const DEFAULT_SUBJECT = "You're being introduced to {{names}}!";
const DEFAULT_BODY = '<p>Hi {{firstName}}!</p>'
  + '<p></p>'
  + "<p>Both you and {{names}} have said you'd like to connect!&nbsp;</p>"
  + '<p></p>'
  + "<p><strong>We've introduced you over messenger!</strong></p>"
  + '<p></p>'
  + '{{suggestedProfileBlock}}'
  + '<p></p>'
  + '{{chatButton_Click here to start chatting}}'
  + '<p></p>'
  + "<p>This link works for 24 hours — after that you'll need to log in to keep the conversation going.</p>"
  + '<div style="font-size:16px;line-height:1.6;color:#1e293b;font-family:-apple-system,sans-serif;margin-top:2.5em;">'
  + '<p style="margin:0 0 0.6em;">Thanks,<br>James</p>'
  + '<p style="margin:0 0 0.6em;padding-top:0.6em;border-top:1px solid #e2e8f0;color:#64748b;font-size:14px;">Founder, Interconnector — Berlin Creatives</p>'
  + '<p style="margin:0;font-size:13px;">'
  + '<a href="https://www.linkedin.com/in/jamesmurraypeachey/" style="color:#4338ca;text-decoration:none;">LinkedIn</a>'
  + '&nbsp;·&nbsp;'
  + '<a href="https://theinterconnector.com/about#our-team" style="color:#4338ca;text-decoration:none;">Interconnector Profile</a>'
  + '</p>'
  + '</div>';

// Phase 2 of "Matched Intros" (see the design conversation this was built
// from): browse mutually-accepted clusters (phase 1), select some, and
// send each person their own personalized intro email with a scoped,
// time-limited chat link (see conversationAccessToken.js) — deliberately
// NOT a real account login, see backend/routes/messengerIntro.js.
const MatchedIntrosPanel = ({ communityId, communityTitle, authedFetch }) => {
  const [clusters, setClusters] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [senderAddress, setSenderAddress] = useState('');

  const [composeOpen, setComposeOpen] = useState(false);
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [body, setBody] = useState(DEFAULT_BODY);
  const [excludedRecipientIds, setExcludedRecipientIds] = useState(() => new Set());
  const [overrideVerification, setOverrideVerification] = useState(false);
  const [overrideNote, setOverrideNote] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [sendResult, setSendResult] = useState(null);

  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);

  // Content-based, not position-based — a given round + exact member set
  // can only ever correspond to one cluster entry (pairs are always
  // length 2, group cliques always length 3+, so there's no shape
  // collision either). Deliberately NOT indexed by array position: this
  // same key is computed against three different arrays at different
  // points (groupClusters, pairClusters, and the full unfiltered
  // clusters list for send-time lookup) — an index-based key would mean
  // something different in each one and silently fail to match.
  const clusterKey = (cluster) => `${cluster.roundId || 'no-round'}:${cluster.members.map((m) => m.userId).sort().join(',')}`;

  const fetchClusters = useCallback(async () => {
    if (!communityId) return;
    setLoading(true);
    setError('');
    setSelectedKeys(new Set());
    setExcludedRecipientIds(new Set());
    setComposeOpen(false);
    setSendResult(null);
    setShowHistory(false);
    setHistory(null);
    try {
      const data = await authedFetch(`/admin/suggestions/communities/${communityId}/matched-intros`);
      setClusters(data.clusters || []);
    } catch (err) {
      setError(err.message || 'Failed to load matched intros.');
      setClusters(null);
    } finally {
      setLoading(false);
    }
  }, [communityId, authedFetch]);

  useEffect(() => { fetchClusters(); }, [fetchClusters]);

  useEffect(() => {
    if (!communityId) return;
    authedFetch('/admin/email/communities')
      .then((data) => {
        const match = (data.communities || []).find((c) => String(c._id) === String(communityId));
        setSenderAddress(match?.senderAddress || '');
      })
      .catch(() => setSenderAddress(''));
  }, [communityId, authedFetch]);

  const fetchHistory = useCallback(async () => {
    if (!communityId) return;
    setHistoryLoading(true);
    try {
      const data = await authedFetch(`/admin/suggestions/communities/${communityId}/matched-intros/history`);
      setHistory(data.history || []);
    } catch (err) {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [communityId, authedFetch]);

  const toggleHistory = () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next && history === null) fetchHistory();
  };

  const toggleSelected = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleExcludedRecipient = (userId) => {
    setExcludedRecipientIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const handleSend = async () => {
    if (overrideVerification && !overrideNote.trim()) {
      setSendError('A note is required when overriding verification.');
      return;
    }
    setSending(true);
    setSendError('');
    setSendResult(null);
    try {
      const data = await authedFetch(`/admin/suggestions/communities/${communityId}/matched-intros/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clusters: selectedClusterObjects.map((cluster) => ({
            members: cluster.members.map((m) => m.userId),
            roundId: cluster.roundId,
            isGroupIntro: cluster.isGroupIntro,
          })),
          subject, body, overrideVerification, overrideNote,
          excludedRecipientUserIds: [...excludedRecipientIds],
        }),
      });
      setSendResult(data);
      fetchClusters();
    } catch (err) {
      setSendError(err.message || 'Failed to send.');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="as-loading">Loading matched intros…</div>;
  if (error) return <div className="as-error">{error}</div>;
  if (!clusters) return null;

  const groupClusters = clusters.filter((c) => c.isGroupIntro);
  const pairClusters = clusters.filter((c) => !c.isGroupIntro);
  const selectedClusterObjects = clusters
    .filter((cluster) => selectedKeys.has(clusterKey(cluster)));
  // Everyone across every selected cluster, deduped — the same person can
  // appear in more than one selected cluster (e.g. a group AND one of its
  // own pairs), so this is who the exclusion table and preview both need
  // to reason about, not the clusters themselves.
  const allSelectedMembersById = new Map();
  selectedClusterObjects.forEach((cluster) => {
    cluster.members.forEach((m) => { if (!allSelectedMembersById.has(m.userId)) allSelectedMembersById.set(m.userId, m); });
  });
  const allSelectedMembers = [...allSelectedMembersById.values()];

  return (
    <div className="as-matched-intros">
      <h2 className="as-section-title">{communityTitle} — Matched Intros ({clusters.length})</h2>
      {clusters.length === 0 && (
        <div className="as-empty">No mutually-accepted matches for this community yet.</div>
      )}

      {groupClusters.length > 0 && (
        <>
          <div className="as-card-meta" style={{ marginBottom: '0.5rem' }}>
            Groups — everyone involved is independently mutually-accepted AND consented to being grouped together. This is offered as well as, not instead of, the 1:1 pairs below — group consent doesn't cancel out a 1:1 option.
          </div>
          <div className="as-signup-list as-signup-list--grid" style={{ marginBottom: '1.5rem' }}>
            {groupClusters.map((cluster) => {
              const key = clusterKey(cluster);
              const isSelected = selectedKeys.has(key);
              return (
                <div
                  key={key}
                  className={`as-signup-card as-signup-card--clickable${isSelected ? ' as-signup-card--selected' : ''}`}
                  onClick={() => toggleSelected(key)}
                >
                  <div className="as-signup-select">
                    <input type="checkbox" checked={isSelected} onChange={() => toggleSelected(key)} onClick={(e) => e.stopPropagation()} />
                  </div>
                  <ClusterPhotos members={cluster.members} />
                  <div>
                    <strong>{clusterNames(cluster.members)}</strong>
                    <div className="as-card-meta">Group intro · {cluster.members.length} people</div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {pairClusters.length > 0 && (
        <>
          <div className="as-card-meta" style={{ marginBottom: '0.5rem' }}>
            1:1 pairs — every mutually-accepted pair, always shown here regardless of any group option above:
          </div>
          <div className="as-signup-list as-signup-list--grid">
            {pairClusters.map((cluster) => {
              const key = clusterKey(cluster);
              const isSelected = selectedKeys.has(key);
              return (
                <div
                  key={key}
                  className={`as-signup-card as-signup-card--clickable${isSelected ? ' as-signup-card--selected' : ''}`}
                  onClick={() => toggleSelected(key)}
                >
                  <div className="as-signup-select">
                    <input type="checkbox" checked={isSelected} onChange={() => toggleSelected(key)} onClick={(e) => e.stopPropagation()} />
                  </div>
                  <ClusterPhotos members={cluster.members} />
                  <div>
                    <strong>{clusterNames(cluster.members)}</strong>
                    <div className="as-card-meta">1:1 pair</div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {clusters.length > 0 && (
        <div className="as-activity-toolbar" style={{ marginTop: '1.5rem' }}>
          <button
            type="button"
            className="as-btn as-btn--approve"
            disabled={selectedKeys.size === 0}
            onClick={() => setComposeOpen((v) => !v)}
          >
            Compose intro email ({selectedKeys.size} selected)
          </button>
        </div>
      )}

      {composeOpen && (
        <div className="as-suggestion-body-preview" style={{ marginTop: '1rem', padding: '1rem' }}>
          {allSelectedMembers.length > 1 && (
            <div style={{ marginBottom: '1rem' }}>
              <div className="as-card-meta" style={{ marginBottom: '0.4rem' }}>
                Who actually gets an email — everyone selected above is still part of the conversation(s) either way. Click a name to move it.
              </div>
              <table className="as-ab-table">
                <thead>
                  <tr>
                    <th>Being sent</th>
                    <th>Not being sent</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ verticalAlign: 'top' }}>
                      {allSelectedMembers.filter((m) => !excludedRecipientIds.has(m.userId)).map((m) => (
                        <button
                          key={m.userId}
                          type="button"
                          className="as-filter-tab"
                          style={{ margin: '0.2rem' }}
                          onClick={() => toggleExcludedRecipient(m.userId)}
                          title="Click to stop emailing this person (still part of the conversation)"
                        >
                          {m.firstName || '(no name)'}
                        </button>
                      ))}
                    </td>
                    <td style={{ verticalAlign: 'top' }}>
                      {allSelectedMembers.filter((m) => excludedRecipientIds.has(m.userId)).map((m) => (
                        <button
                          key={m.userId}
                          type="button"
                          className="as-filter-tab"
                          style={{ margin: '0.2rem' }}
                          onClick={() => toggleExcludedRecipient(m.userId)}
                          title="Click to email this person again"
                        >
                          {m.firstName || '(no name)'}
                        </button>
                      ))}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          <TemplateLibrary authedFetch={authedFetch} subject={subject} body={body} onLoad={({ subject: s, body: b }) => { setSubject(s); setBody(b); }} />

          <div className="as-email-field" style={{ marginTop: '0.75rem' }}>
            <label>Subject</label>
            <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div className="as-email-field" style={{ marginTop: '0.75rem' }}>
            <label>
              Body — placeholders, replaced per-recipient:
            </label>
            <ul style={{ margin: '0.3rem 0 0.5rem', paddingLeft: '1.2rem' }} className="as-card-meta">
              <li><code>{'{{firstName}}'}</code> — this recipient's own first name</li>
              <li><code>{'{{names}}'}</code> — everyone else in that cluster (e.g. "Charlie & Alfie")</li>
              <li><code>{'{{chatLink}}'}</code> — their own scoped, 24h chat link as a plain URL (not a real login — see the design notes), for hyperlinking your own text</li>
              <li><code>{'{{chatButton_<label>}}'}</code> — a big styled button linking to the chat, with your own wording, e.g. <code>{'{{chatButton_Say hi now}}'}</code></li>
              <li><code>{'{{suggestedProfileBlock}}'}</code> — a small photo card for everyone else in that cluster (no button, just a visual reminder of who they're chatting with)</li>
              <li><code>{'{{allProfileBlock}}'}</code> — a small photo card for everyone in that cluster, including this recipient themselves</li>
            </ul>
            <RichBodyEditor
              value={body}
              onChange={setBody}
              placeholder="Hi {{firstName}}, ... {{chatLink}}"
            />
          </div>

          <div className="as-email-field" style={{ marginTop: '0.75rem' }}>
            <label>
              <input type="checkbox" checked={overrideVerification} onChange={(e) => setOverrideVerification(e.target.checked)} style={{ marginRight: '0.4rem' }} />
              Override verification requirements
            </label>
            {overrideVerification && (
              <textarea
                rows={2}
                value={overrideNote}
                onChange={(e) => setOverrideNote(e.target.value)}
                placeholder="What you're overriding and why / what you've confirmed they have (required)"
                style={{ width: '100%', marginTop: '0.4rem' }}
              />
            )}
          </div>

          {(() => {
            const sendCount = allSelectedMembers.filter((m) => !excludedRecipientIds.has(m.userId)).length;
            const fullyExcludedClusters = selectedClusterObjects.filter(
              (cluster) => cluster.members.every((m) => excludedRecipientIds.has(m.userId))
            );
            return (
              <div className="as-email-preview" style={{ marginTop: '1rem' }}>
                <div className="as-card-meta">
                  Preview
                  <button type="button" className="as-filter-tab" style={{ marginLeft: '0.75rem' }} onClick={() => setComposeOpen(false)} disabled={sending}>
                    Cancel
                  </button>
                  <button type="button" className="as-btn as-btn--approve" style={{ marginLeft: '0.75rem' }} onClick={handleSend} disabled={sending || sendCount === 0}>
                    {sending ? 'Sending…' : `📤 Send (${sendCount})`}
                  </button>
                </div>

                {fullyExcludedClusters.length > 0 && (
                  <ul className="as-suggestion-warnings">
                    {fullyExcludedClusters.map((cluster) => (
                      <li key={clusterNames(cluster.members)}>
                        ⚠ {clusterNames(cluster.members)} — everyone in this cluster is excluded, so no email will be sent for it (still logged/conversation-created as usual).
                      </li>
                    ))}
                  </ul>
                )}

                {sendError && <div className="as-error" style={{ marginTop: '0.5rem' }}>{sendError}</div>}

                <MatchedIntroPreviewTabs
                  selectedClusters={selectedClusterObjects}
                  subject={subject}
                  body={body}
                  excludedRecipientIds={excludedRecipientIds}
                  senderAddress={senderAddress}
                />
              </div>
            );
          })()}
        </div>
      )}

      {sendResult && (
        <div className="as-card-meta" style={{ marginTop: '1rem' }}>
          Sent {sendResult.results?.sent || 0}, failed {sendResult.results?.failed || 0}, across {sendResult.clustersSent || 0} cluster(s).
          {sendResult.skipped?.length > 0 && (
            <div style={{ marginTop: '0.4rem', color: '#b91c1c' }}>
              Skipped {sendResult.skipped.length} cluster(s) for failed verification (no override checked) — re-select and check the override box to send anyway.
              {sendResult.skipped.some((s) => (s.ineligible || []).some((r) => r.reason === 'ID_NOT_VERIFIED')) && ' Some haven\u2019t confirmed their age (face scan) yet.'}
            </div>
          )}
        </div>
      )}

      <div className="as-activity-toolbar" style={{ marginTop: '1.5rem' }}>
        <button type="button" className="as-filter-tab" onClick={toggleHistory}>
          {showHistory ? '▾' : '▸'} History
        </button>
      </div>

      {showHistory && (
        <div>
          {historyLoading && <div className="as-loading">Loading…</div>}
          {history && history.length === 0 && <div className="as-empty">No matched intros sent yet for this community.</div>}
          {history && history.length > 0 && (
            <table className="as-ab-table">
              <thead>
                <tr>
                  <th>Who</th>
                  <th>Type</th>
                  <th>When</th>
                  <th>Sent by</th>
                  <th>Informed</th>
                </tr>
              </thead>
              <tbody>
                {history.map((entry) => (
                  <React.Fragment key={entry._id}>
                    <tr style={{ cursor: 'pointer' }} onClick={() => setExpandedHistoryId(expandedHistoryId === entry._id ? null : entry._id)}>
                      <td>{clusterNames(entry.members)}</td>
                      <td>{entry.isGroupIntro ? 'Group' : '1:1'}</td>
                      <td>{new Date(entry.createdAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                      <td>{entry.createdBy}</td>
                      <td>{entry.members.filter((m) => m.informed).length} / {entry.members.length}</td>
                    </tr>
                    {expandedHistoryId === entry._id && (
                      <tr>
                        <td colSpan={5}>
                          <div className="as-suggestion-recipient-card">
                            <div className="as-card-meta">
                              <strong>Conversation:</strong> {entry.conversationWasReused ? 'reused an existing one' : 'created new'}
                            </div>
                            {entry.verificationOverride?.overridden && (
                              <div className="as-card-meta" style={{ color: '#b91c1c' }}>
                                <strong>Verification overridden</strong> by {entry.verificationOverride.overriddenBy}: {entry.verificationOverride.note}
                              </div>
                            )}
                            <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.2rem' }}>
                              {entry.members.map((m) => (
                                <li key={m.userId} className="as-card-meta">
                                  {m.firstName || m.userId} — {m.informed ? '✓ informed (email sent)' : `— not informed (${m.emailStatus === 'not_sent' ? 'excluded from this send' : m.emailStatus})`}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default MatchedIntrosPanel;
